import React, { useEffect, useRef, useState } from "react";
import { Chip, Select } from "../../../ForBrands/ui/BrandUI";
import { Icon } from "../../../ForBrands/ui/icons";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { optionLabel } from "../hooks/useCascadeFields";
import searchIcon from "../../../../assets/brand/onboarding/search.svg";

// Small CacheShare™ V2 controls that BrandUI does not have yet (Figma Tag,
// filter chip menu, pager, eye toggle, a search field that can be disabled).

export const RemovableTag = ({ children, onRemove, removeLabel }) => (
  <span className="inline-flex h-7 max-w-full items-center gap-[6px] rounded-full bg-bm-subtle pl-[10px] pr-2 bm-body-m text-bm-ink">
    <span className="truncate">{children}</span>
    <button type="button" onClick={onRemove} aria-label={removeLabel} className="text-[11px] leading-none text-bm-tertiary hover:text-bm-ink">
      ✕
    </button>
  </span>
);

// `size="lg"` is the phone field (Input · Mobile with the 16px search icon,
// 2420:2447, 2421:2533): 56px, 12px radius, the ink focus ring and 16px text
// so iOS does not zoom the page when it gets focus (D-g6).
const SEARCH_BOX = {
  md: "flex h-10 items-center gap-2 rounded-[8px] border border-bm-border px-[14px] focus-within:border-bm-orange",
  lg: "flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]",
};
const SEARCH_TEXT = {
  md: "bm-body-m",
  lg: "font-inter text-[16px] leading-[22px]",
};

export const SearchField = ({ value, onChange, placeholder, label, disabled = false, onFocus, onEnter, size = "md" }) => {
  const lg = size === "lg";
  const key = lg ? "lg" : "md";
  return (
    <span className={`${SEARCH_BOX[key]} ${disabled ? "bg-bm-subtle" : "bg-white"}`}>
      {lg ? <img src={searchIcon} alt="" className="h-4 w-4 shrink-0" /> : <Icon name="search" size={14} className="text-bm-muted" />}
      <input
        type="text"
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) onEnter();
        }}
        placeholder={placeholder}
        aria-label={label}
        enterKeyHint={lg ? "search" : undefined}
        className={`bm-bare min-w-0 flex-1 bg-transparent ${SEARCH_TEXT[key]} text-bm-ink outline-none placeholder:text-bm-placeholder disabled:cursor-not-allowed`}
      />
    </span>
  );
};

// Closes a popover on an outside press or Escape.
export const useDismiss = (open, onDismiss) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onDismiss();
    };
    const onKey = (e) => e.key === "Escape" && onDismiss();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onDismiss]);
  return ref;
};

// A filter chip (Figma: Chip with chevron) that opens its option list.
// `fill` (the phone roster head) anchors the list to the chip row instead of
// the chip: it opens under the chip at the row's full width, so a chip that
// wraps to either edge never pushes its list off the screen. The row needs
// `relative`. The list sits one layer below the phone shell's sticky top bar
// and fixed bottom nav (z-30), so it scrolls under them.
export const FilterMenu = ({ label, value, options, onChange, fill = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };
  return (
    <div ref={ref} className={fill ? undefined : "relative"}>
      <Chip chevron active={!!value} onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} className={value ? "[&_img]:invert" : ""}>
        {value ? `${label}: ${value}` : label}
      </Chip>
      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className={
            fill
              ? "absolute inset-x-0 z-20 mt-2 max-h-[260px] overflow-y-auto rounded-[12px] border border-bm-border bg-white py-1 shadow-lg"
              : "absolute left-0 z-30 mt-2 max-h-[260px] min-w-[180px] overflow-y-auto rounded-[12px] border border-bm-border bg-white py-1 shadow-lg"
          }
        >
          {value && (
            <li>
              <button type="button" onClick={() => pick("")} className="w-full px-4 py-2 text-left bm-label-l text-bm-muted hover:bg-bm-subtle">
                Clear
              </button>
            </li>
          )}
          {options.length === 0 && <li className="px-4 py-2 bm-body-s text-bm-muted">Nothing to filter yet</li>}
          {options.map((o) => (
            <li key={o} role="option" aria-selected={o === value}>
              <button type="button" onClick={() => pick(o)} className={`w-full px-4 py-2 text-left bm-body-m hover:bg-bm-subtle ${o === value ? "font-semibold text-bm-ink" : "text-bm-ink"}`}>
                {o}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// `radius` is 6px on the desktop pager and 8px on the phone one (2415:2458).
const PageButton = ({ children, radius = "rounded-[6px]", ...props }) => (
  <button type="button" className={`flex h-8 w-8 items-center justify-center ${radius} border border-bm-border bg-white text-bm-ink hover:bg-bm-subtle disabled:cursor-not-allowed disabled:text-bm-tertiary`} {...props}>
    {children}
  </button>
);

const PAGE_SIZES = [25, 50, 100];

const PageSizeSelect = ({ size, onSize, className }) => (
  <Select value={size} onChange={(e) => onSize(Number(e.target.value))} className={className} aria-label="Page size">
    {PAGE_SIZES.map((n) => (
      <option key={n} value={n}>
        {n}
      </option>
    ))}
  </Select>
);

const PageNav = ({ page, pages, onPage, radius = "rounded-[6px]" }) => (
  <div className="flex items-center gap-2">
    <PageButton radius={radius} aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
      <Icon name="arrow-left" size={12} />
    </PageButton>
    <span aria-current="page" className={`flex h-8 w-8 items-center justify-center ${radius} bg-bm-ink bm-label-xs text-white`}>
      {page}
    </span>
    <PageButton radius={radius} aria-label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}>
      <Icon name="arrow-right" size={12} />
    </PageButton>
  </div>
);

// Roster foot on phones (2415:2458): the page size as a pill select on its
// own row, then the count with the page buttons. The pill's text is 16px so
// iOS does not zoom the page when it opens (D-g6).
const PHONE_PAGE_SIZE = "!h-10 !w-auto !rounded-full !pl-4 !pr-9 !text-[16px] !font-semibold !leading-[22px]";

const PhonePager = ({ page, pages, from, to, total, size, onPage, onSize }) => (
  <div className="flex flex-col gap-3">
    <label className="flex items-center gap-[10px]">
      <span className="bm-body-s text-bm-muted">Page size</span>
      <PageSizeSelect size={size} onSize={onSize} className={PHONE_PAGE_SIZE} />
    </label>
    <div className="flex items-center justify-between gap-3 px-1 py-[18px]">
      <span className="min-w-0 bm-body-s text-bm-muted">
        {from} to {to} of {total} · Page {page} of {pages}
      </span>
      <div className="shrink-0">
        <PageNav page={page} pages={pages} onPage={onPage} radius="rounded-[8px]" />
      </div>
    </div>
  </div>
);

export const Pager = (props) => {
  const phone = usePhoneTheme();
  if (phone) return <PhonePager {...props} />;
  const { page, pages, from, to, total, size, onPage, onSize } = props;
  return (
    <div className="flex flex-wrap items-center justify-between gap-6 px-5">
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2">
          <span className="bm-body-s text-bm-muted">Page size</span>
          <PageSizeSelect size={size} onSize={onSize} className="!w-[88px]" />
        </label>
        <span className="bm-body-s text-bm-muted">
          {from} to {to} of {total} · Page {page} of {pages}
        </span>
      </div>
      <PageNav page={page} pages={pages} onPage={onPage} />
    </div>
  );
};

// `iconSize` is 18 in the tables and 20 on the phone player cards (2416:2628).
export const EyeToggle = ({ hidden, onToggle, label, iconSize = 18 }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={label}
    className={`flex h-8 w-8 items-center justify-center rounded-full hover:bg-bm-mutedbg ${hidden ? "text-bm-tertiary" : "text-bm-muted"}`}
  >
    <Icon name={hidden ? "eye-off" : "eye"} size={iconSize} />
  </button>
);

// One cascade field (see cascadeFieldSpecs) as a V2 select. `size="lg"` is
// the 56px phone select with 16px text (FormField · Mobile 2420:2417).
export const CascadeSelect = ({ spec, disabled = false, size = "md" }) => (
  <Select
    value={spec.value ?? ""}
    onChange={(e) => spec.onChange(e.target.value)}
    disabled={disabled}
    aria-label={spec.label}
    className={size === "lg" ? "bm-input-lg" : undefined}
  >
    <option value="">{spec.placeholder}</option>
    {spec.groups
      ? spec.groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.name}
              </option>
            ))}
          </optgroup>
        ))
      : spec.options.map((o) => (
          <option key={o.value} value={o.value} disabled={!!o.status && o.status !== "live"}>
            {optionLabel(o)}
          </option>
        ))}
  </Select>
);
